import { ChevronLeft } from 'lucide-react';
import { type ReactElement } from 'react';

const GENRE_KEYS = ['g1', 'g2', 'g3'] as const;
const STAT_KEYS = ['s1', 's2', 's3'] as const;
const TAB_KEYS = ['t1', 't2', 't3', 't4'] as const;
const SYNOPSIS_KEYS = ['sy1', 'sy2', 'sy3', 'sy4', 'sy5'] as const;
const META_KEYS = ['m1', 'm2', 'm3', 'm4', 'm5', 'm6'] as const;
const RELATED_KEYS = ['r1', 'r2', 'r3', 'r4', 'r5'] as const;

function BackHeaderSkeleton(): ReactElement {
  return (
    <div
      className="absolute left-0 right-0 top-0 z-40"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="mx-auto max-w-[1400px] px-3 sm:px-6 lg:px-8">
        <div className="py-4 sm:py-6">
          <div className="inline-flex items-center gap-2 rounded-full border border-border/40 bg-background/70 px-4 py-1.5 shadow-sm backdrop-blur-md">
            <ChevronLeft className="h-4 w-4 text-muted-foreground/60" />
            <span className="text-sm text-muted-foreground/60 sm:text-base">
              Back to Home
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function HeroBannerSkeleton(): ReactElement {
  return (
    <div className="skeleton-shimmer relative h-[290px] w-full overflow-hidden sm:h-[360px] lg:h-[480px]">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/40 to-background/80" />
    </div>
  );
}

function PosterSkeleton(): ReactElement {
  return (
    <div className="relative mx-auto w-fit flex-shrink-0 lg:mx-0">
      <div className="skeleton-shimmer w-[160px] overflow-hidden rounded-xl border border-border/60 shadow-2xl sm:w-[190px] lg:w-[230px]">
        <div className="aspect-[2/3] w-full" />
      </div>
      <div className="absolute -right-3 top-2 h-7 w-14 rounded-l-full bg-background/90 shadow-lg sm:-right-4 sm:top-4 sm:h-9 sm:w-16" />
    </div>
  );
}

function TitleBlockSkeleton(): ReactElement {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="skeleton-shimmer h-6 w-12 rounded-full" />
        {GENRE_KEYS.map((key) => (
          <div key={key} className="skeleton-shimmer h-6 w-20 rounded-full" />
        ))}
      </div>
      <div className="skeleton-shimmer h-8 w-3/4 rounded sm:h-10 md:h-12" />
      <div className="skeleton-shimmer h-4 w-1/2 rounded" />
    </div>
  );
}

function ActionsSkeleton(): ReactElement {
  return (
    <div className="flex flex-wrap gap-2">
      <div className="skeleton-shimmer h-10 w-32 rounded-full" />
      <div className="skeleton-shimmer h-10 w-10 rounded-full" />
      <div className="skeleton-shimmer h-10 w-10 rounded-full" />
    </div>
  );
}

function QuickStatsSkeleton(): ReactElement {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-4">
      {STAT_KEYS.map((key) => (
        <div
          key={key}
          className="skeleton-shimmer flex items-center gap-2 rounded-lg px-3 py-2 sm:gap-3 sm:rounded-xl sm:px-4 sm:py-3"
        >
          <div className="skeleton-shimmer h-4 w-4 rounded sm:h-5 sm:w-5" />
          <div className="flex-1 space-y-1.5">
            <div className="skeleton-shimmer h-3 w-12 rounded" />
            <div className="skeleton-shimmer h-3 w-20 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

function HeroCardSkeleton(): ReactElement {
  return (
    <div className="relative z-10 -mt-[4.6rem] sm:-mt-[5.8rem] lg:-mt-[6.9rem]">
      <div className="mx-auto max-w-[1400px] px-3 sm:px-6 lg:px-8">
        <div className="rounded-2xl border border-border/60 bg-background/85 p-4 shadow-2xl backdrop-blur-md sm:p-6 lg:p-8">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:gap-8">
            <PosterSkeleton />
            <div className="min-w-0 flex-1 space-y-4 sm:space-y-5">
              <TitleBlockSkeleton />
              <ActionsSkeleton />
              <QuickStatsSkeleton />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TabsBarSkeleton(): ReactElement {
  return (
    <div className="flex flex-wrap gap-2 border-b border-border/40 pb-2">
      {TAB_KEYS.map((key, index) => (
        <div
          key={key}
          className={`skeleton-shimmer h-9 rounded-md ${
            index === 0 ? 'w-24' : 'w-20'
          }`}
        />
      ))}
    </div>
  );
}

function SynopsisSkeleton(): ReactElement {
  return (
    <section className="space-y-3 rounded-xl border border-border/60 bg-card/60 p-4 sm:p-6">
      <div className="skeleton-shimmer h-5 w-32 rounded" />
      <div className="space-y-2">
        {SYNOPSIS_KEYS.map((key) => (
          <div key={key} className="skeleton-shimmer h-3 w-full rounded" />
        ))}
        <div className="skeleton-shimmer h-3 w-3/4 rounded" />
      </div>
    </section>
  );
}

function MetaSidebarSkeleton(): ReactElement {
  return (
    <aside className="space-y-3 rounded-xl border border-border/60 bg-card/60 p-4 sm:p-6">
      <div className="skeleton-shimmer h-5 w-24 rounded" />
      <div className="space-y-3">
        {META_KEYS.map((key) => (
          <div key={key} className="flex items-center justify-between gap-3">
            <div className="skeleton-shimmer h-3 w-20 rounded" />
            <div className="skeleton-shimmer h-3 w-24 rounded" />
          </div>
        ))}
      </div>
    </aside>
  );
}

function RelatedSkeleton(): ReactElement {
  return (
    <section className="space-y-3">
      <div className="skeleton-shimmer h-5 w-40 rounded" />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
        {RELATED_KEYS.map((key) => (
          <div key={key} className="space-y-2">
            <div className="skeleton-shimmer aspect-[2/3] w-full rounded-lg border border-border/40" />
            <div className="skeleton-shimmer h-3 w-4/5 rounded" />
            <div className="skeleton-shimmer h-3 w-1/2 rounded" />
          </div>
        ))}
      </div>
    </section>
  );
}

function TabsContentSkeleton(): ReactElement {
  return (
    <div className="relative">
      <div className="mx-auto mt-4 max-w-[1400px] px-3 pb-16 sm:mt-6 sm:px-6 lg:px-8">
        <div className="flex flex-col space-y-6">
          <TabsBarSkeleton />
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
            <div className="space-y-6">
              <SynopsisSkeleton />
              <RelatedSkeleton />
            </div>
            <MetaSidebarSkeleton />
          </div>
        </div>
      </div>
    </div>
  );
}

export function AnimeDetailSkeleton(): ReactElement {
  // RouteSkeleton owns the delay; paint instantly here so subsequent
  // internal re-mounts don't introduce a reset gap.
  return (
    <div className="relative min-h-screen bg-gradient-to-b from-background via-background/95 to-background">
      <BackHeaderSkeleton />
      <section className="relative pb-6 sm:pb-8 lg:pb-10">
        <HeroBannerSkeleton />
        <HeroCardSkeleton />
      </section>
      <TabsContentSkeleton />
    </div>
  );
}
